import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye } from 'lucide-react'
import { type EventData } from './event-constants'
import { StatusBadge, DirectionIcon, DirectionBadge, JsonViewer, HttpCodeBadge } from './event-ui'

interface EventDetailsDialogProps {
  event: EventData
  trigger?: React.ReactNode
}

export function EventDetailsDialog({ event, trigger }: EventDetailsDialogProps) {
  const [open, setOpen] = useState(false)
  const title = event.direction === 'inbound' ? 'Detalhes do Evento' : 'Detalhes do Envio'

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Eye className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg overflow-hidden flex flex-col gap-0">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <DirectionIcon direction={event.direction} />
            {title}
          </SheetTitle>
          <SheetDescription>
            <Badge variant="outline" className="font-mono text-xs">
              {event.id}
            </Badge>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-8 pr-2 px-4">
          <StatusSection event={event} />
          <DetailsSection event={event} />
          {event.errorMessage && <ErrorSection message={event.errorMessage} />}
          <PayloadSection event={event} />
          <ResponseSection event={event} />
        </div>
      </SheetContent>
    </Sheet>
  )
}

function StatusSection({ event }: { event: EventData }) {
  return (
    <section className="space-y-3">
      <SectionTitle>
        Status da {event.direction === 'inbound' ? 'Recepção' : 'Entrega'}
      </SectionTitle>
      <div className="flex items-center gap-3">
        <StatusBadge status={event.status} />
        {event.responseCode && (
          <>
            <Divider />
            <span className="text-sm">
              HTTP Code: <HttpCodeBadge code={event.responseCode} />
            </span>
          </>
        )}
        {event.attemptCount && (
          <>
            <Divider />
            <span className="text-sm text-muted-foreground">
              {event.attemptCount} tentativa{event.attemptCount > 1 ? 's' : ''}
            </span>
          </>
        )}
      </div>
    </section>
  )
}

function DetailsSection({ event }: { event: EventData }) {
  return (
    <section className="space-y-3">
      <SectionTitle>Detalhes da Requisição</SectionTitle>
      <div className="grid gap-3">
        {event.url && <DetailRow label="Endpoint" value={event.url} mono />}
        {!event.url && event.direction === 'outbound' && (
          <DetailRow label="Destino" value="Klaviyo API" italic />
        )}
        <DetailRow label="Evento">
          <Badge variant="secondary" className="font-mono text-xs">
            {event.eventType || 'N/A'}
          </Badge>
        </DetailRow>
        <DetailRow label="Direção">
          <DirectionBadge direction={event.direction} />
        </DetailRow>
        <DetailRow label="Data" value={new Date(event.createdAt).toLocaleString('pt-BR')} />
      </div>
    </section>
  )
}

function ErrorSection({ message }: { message: string }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider">Erro</h3>
      <div className="text-sm bg-red-50 text-red-800 dark:bg-red-950/50 dark:text-red-400 px-4 py-3 rounded-lg border border-red-200 dark:border-red-900">
        {message}
      </div>
    </section>
  )
}

function PayloadSection({ event }: { event: EventData }) {
  const label = event.direction === 'inbound' ? 'Recebido' : 'Enviado'
  return (
    <section className="space-y-3">
      <SectionTitle>Payload {label}</SectionTitle>
      <JsonViewer data={event.payload} />
    </section>
  )
}

function ResponseSection({ event }: { event: EventData }) {
  const label = event.direction === 'inbound' ? 'Ação Executada' : 'Resposta do Servidor'
  return (
    <section className="space-y-3">
      <SectionTitle>{label}</SectionTitle>
      <JsonViewer data={event.response} emptyText="Nenhum corpo de resposta retornado" />
    </section>
  )
}

// Helper components
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
      {children}
    </h3>
  )
}

function Divider() {
  return <span className="text-muted-foreground">|</span>
}

function DetailRow({
  label,
  value,
  mono,
  italic,
  children,
}: {
  label: string
  value?: string
  mono?: boolean
  italic?: boolean
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-sm text-muted-foreground w-20 shrink-0">{label}:</span>
      {children || (
        <span
          className={`text-sm ${mono ? 'font-mono break-all' : ''} ${italic ? 'italic text-muted-foreground' : ''}`}
        >
          {value}
        </span>
      )}
    </div>
  )
}
